import { ReactNode } from 'react';
import { colors } from 'ui/theme/colors';
import { useTreeViewContext } from './context/TreeViewContext';
import { detectNodeType } from './detectNodeType';
import { ComponentTypes, useGetTreeComponent } from './hooks';
import { isSimpleValue } from './isSimpleValue';
import { MemoTreeBranch } from './TreeBranch';
import { MemoTreeLeaf } from './TreeLeaf';
import { AtomicEntry, NestedTableNodeTree, NodeTrees, PossibleNodeTypes } from './types';

export type TreeRootProps = {
  src: NodeTrees | NestedTableNodeTree[];
};

export const TreeRoot = ({ src }: TreeRootProps) => {
  const { sort, decompose } = useTreeViewContext();

  const RootComponent = useGetTreeComponent(ComponentTypes.root);

  return (
    <RootComponent>
      {decompose({
        src,
        sort,
        color: colors.white,
        render: ({
          isLastElement,
          label: childLabel = '',
          value: childValue,
          path: key,
          row,
          isLeaf,
          color: childColor,
        }) => {
          const valueType = detectNodeType(childValue as PossibleNodeTypes);
          const path = key || (childLabel as string);

          if (isLeaf || isSimpleValue(valueType)) {
            return (
              <MemoTreeLeaf
                isLast={isLastElement}
                key={path}
                label={childLabel}
                type={valueType}
                value={childValue as AtomicEntry | NonNullable<ReactNode>}
                color={childColor}
              />
            );
          }

          return (
            <MemoTreeBranch
              isLast={isLastElement}
              key={path}
              label={childLabel}
              type={valueType}
              value={childValue as Exclude<PossibleNodeTypes, AtomicEntry> | NestedTableNodeTree[]}
              path={path}
              row={row}
              color={childColor}
              depth={0}
            />
          );
        },
      })}
    </RootComponent>
  );
};
